import {
  getTextFromChars,
  type Block,
  type BlockCloze,
  type Blocks,
  type Question,
} from '@w/content-blocks/src/block'
import { answerPlainText } from '@w/content-blocks/src/parser'
import {
  matchesAnswerRegexp,
  normalizeAnswerText,
} from '../pages/training/cloze-answer-grading'

/** Scores training answers independently of their rendering. */

export type ActiveTrainingBlock = Question | BlockCloze

export interface TrainingViewerState {
  textValue: string
  audio?: Blob
  selectedIndex: number | null
  selectedIndices: number[]
}

export function makeTrainingViewerState(): TrainingViewerState {
  return {
    textValue: '',
    selectedIndex: null,
    selectedIndices: [],
  }
}

// One qid = one question; its excerpt is every top-level block holding a part.
export function getTrainingExcerptBlocks(
  blocks: Block[],
  qid: string,
): Block[] {
  const matches = blocks.filter((block) => _containsQid(block, qid))
  return matches.length > 0 ? matches : blocks
}

/** Every answer part (question/cloze block) of the qid, in document order. */
export function getTrainingParts(
  blocks: readonly Block[],
  qid: string,
): ActiveTrainingBlock[] {
  return _questionLikeBlocks(blocks).filter(block => block.qid === qid)
}

/** Every sc/mc block in document order, whatever its qid. */
export function trainingChoices(blocks: readonly Block[]) {
  return _questionLikeBlocks(blocks).filter(block => isTrainingChoice(block))
}

function _questionLikeBlocks(blocks: readonly Block[]): ActiveTrainingBlock[] {
  const found: ActiveTrainingBlock[] = []
  const visit = (block: Block) => {
    if (block.type === 'cloze' || block.type === 'question') found.push(block)
    for (const child of _childBlocks(block)) visit(child)
  }
  for (const block of blocks) visit(block)
  return found
}

/**
 * Reveal: every part of the qid shows its answer, marked in the same document
 * order getTrainingParts reads (partMarks[i] belongs to parts[i]).
 */
export function revealTrainingParts(
  blocks: readonly Block[],
  qid: string,
  mark: number | null,
  partMarks?: readonly number[],
): Block[] {
  let part = 0
  const reveal = (block: Block): Block => {
    if ((block.type === 'cloze' || block.type === 'question') && block.qid === qid) {
      const partMark = partMarks?.[part++] ?? mark
      return _mapChildBlocks({ ...block, $showAnswer: true, ...(partMark === null ? {} : { $mark: partMark }) } as Block, child => reveal(child))
    }
    return _mapChildBlocks(block, child => reveal(child))
  }
  return blocks.map(block => reveal(block))
}

// Same child order as _childBlocks: children, question, explanation, options.
function _mapChildBlocks(block: Block, map: (block: Block) => Block): Block {
  let next = 'children' in block && Array.isArray(block.children)
    ? { ...block, children: (block.children as Blocks).map(child => map(child)) } as Block
    : block
  if (next.type === 'question') {
    next = {
      ...next,
      ...(next.question && { question: next.question.map(child => map(child)) }),
      ...(next.explanation && { explanation: next.explanation.map(child => map(child)) }),
      ...((next.subtype === 'sc' || next.subtype === 'mc') && { opts: next.opts.map(option => _optionBlocks(option).map(child => map(child))) }),
    } as Block
  }
  return next
}

function _childBlocks(block: Block): Blocks {
  const children = 'children' in block && Array.isArray(block.children) ? block.children as Blocks : []
  if (block.type !== 'question') return children
  return [
    ...children,
    ...(block.question ?? []),
    ...(block.explanation ?? []),
    ...(block.subtype === 'sc' || block.subtype === 'mc' ? block.opts.flatMap(_optionBlocks) : []),
  ]
}

export function getTrainingPromptBlock(
  blocks: Block[],
  qid: string,
): ActiveTrainingBlock | null {
  for (const block of blocks) {
    const match = _findQuestionLikeBlock(block, qid)
    if (match) return match
  }

  return null
}

/** A Fisher-Yates order of `length` options: the original index shown at each position. */
export function shuffledOptionOrder(length: number, random: () => number = Math.random): number[] {
  const order = Array.from({ length }, (_, index) => index)
  for (let index = order.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(random() * (index + 1))
    const current = order[index]
    const swap = order[swapIndex]
    if (current === undefined || swap === undefined) continue
    order[index] = swap
    order[swapIndex] = current
  }
  return order
}

// Every choice rendered in a flashcard gets its own shuffle, whatever its qid;
// answers follow their options.
export function shuffleTrainingChoiceOptions(
  blocks: Block[],
  random: () => number = Math.random,
): Block[] {
  if (trainingChoices(blocks).length === 0) return blocks
  const shuffledBlocks = structuredClone(blocks)
  for (const choice of trainingChoices(shuffledBlocks)) {
    const order = shuffledOptionOrder(choice.opts.length, random)
    const remapAnswer = (originalIndex: number) => order.indexOf(originalIndex)
    choice.opts = order.map(originalIndex => choice.opts[originalIndex] ?? [])
    if (choice.subtype === 'sc') {
      choice.answer = remapAnswer(choice.answer)
    } else {
      choice.answer = choice.answer
        .map(remapAnswer)
        .toSorted((left, right) => left - right)
    }
  }
  return shuffledBlocks
}

export function isTrainingChoice(block: ActiveTrainingBlock): block is Extract<Question, { subtype: 'sc' | 'mc' }> {
  return block.type === 'question' && (block.subtype === 'sc' || block.subtype === 'mc')
}

export function canSubmitTrainingBlock(
  block: ActiveTrainingBlock,
  state: TrainingViewerState,
) {
  if (block.type === 'cloze') return state.textValue.trim().length > 0
  if (block.subtype === 'sa') return state.textValue.trim().length > 0
  if (block.subtype === 'la') return state.textValue.trim().length > 0
  if (block.subtype === 'sr') return state.audio !== undefined
  if (block.subtype === 'sc') return state.selectedIndex !== null
  if (block.subtype === 'mc') return state.selectedIndices.length > 0
  return false
}

export function scoreTrainingBlock(
  block: ActiveTrainingBlock,
  state: TrainingViewerState,
): { score: number } | null {
  if (block.type === 'cloze') return null

  if (block.subtype === 'sa') {
    const score = _scoreShortAnswer(block, state.textValue) ? 1 : 0
    return { score }
  }

  if (block.subtype === 'sr') return null

  if (block.subtype === 'sc') {
    if (state.selectedIndex === null) return null
    const score = state.selectedIndex === block.answer ? 1 : 0
    return { score }
  }

  if (block.subtype === 'mc') {
    const chosen = [...state.selectedIndices].toSorted(
      (left, right) => left - right,
    )
    const expected = [...block.answer].toSorted((left, right) => left - right)
    const score =
      chosen.length === expected.length &&
      chosen.every((value, index) => value === expected[index])
        ? 1
        : 0

    return { score }
  }

  return null
}

/**
 * Grade a short-answer response against the saved answers.
 * - answerRegexp: if set and it matches, the answer is correct regardless of the
 *   strict/bag-of-words check (same regex escape hatch clozes use).
 * - strict: exact match against any saved answer, keeping case and punctuation;
 *   only surrounding/internal whitespace is collapsed.
 * - default: bag-of-words — the normalized word SET must equal a saved answer's
 *   word set (same words, any order; no extras, none missing). Normalization
 *   (shared with cloze grading) lowercases and strips punctuation.
 */
function _scoreShortAnswer(
  block: Extract<Question, { subtype: 'sa' }>,
  value: string,
): boolean {
  if (
    block.answerRegexp &&
    matchesAnswerRegexp(value, block.answerRegexp) === true
  ) {
    return true
  }

  if (block.strict) {
    const typed = _collapseWhitespace(value)
    return block.answer.some(
      (expected) => typed === _collapseWhitespace(answerPlainText(expected)),
    )
  }

  const typedWords = _normalizedWordSet(value)
  return block.answer.some((expected) =>
    _sameWordSet(typedWords, _normalizedWordSet(answerPlainText(expected))),
  )
}

function _collapseWhitespace(text: string): string {
  return text.trim().replaceAll(/\s+/g, ' ')
}

function _normalizedWordSet(text: string): Set<string> {
  return new Set(normalizeAnswerText(text).split(' ').filter(Boolean))
}

function _sameWordSet(left: Set<string>, right: Set<string>): boolean {
  if (left.size !== right.size) return false
  for (const word of left) {
    if (!right.has(word)) return false
  }
  return true
}

export function describeTrainingAnswer(block: ActiveTrainingBlock) {
  if (block.type === 'cloze') return _plainText(block.children)
  if (block.subtype === 'sa') return block.answer.map(answerPlainText).join(', ')
  if (block.subtype === 'sr') return block.answer.transcript
  if (block.subtype === 'sc') {
    return _plainText(_optionBlocks(block.opts[block.answer]))
  }
  if (block.subtype === 'mc') {
    return block.answer
      .map((index) => _plainText(_optionBlocks(block.opts[index])))
      .filter(Boolean)
      .join(', ')
  }
  if (block.explanation) return _plainText(block.explanation)
  if (block.question) return _plainText(block.question)
  return ''
}

function _findQuestionLikeBlock(
  block: Block,
  qid: string,
): ActiveTrainingBlock | null {
  if (block.type === 'cloze' && block.qid === qid) return block
  if (block.type === 'question' && block.qid === qid) return block

  for (const child of _childBlocks(block)) {
    const match = _findQuestionLikeBlock(child, qid)
    if (match) return match
  }

  return null
}

function _containsQid(block: Block, qid: string) {
  return _findQuestionLikeBlock(block, qid) !== null
}

function _plainText(blocks: Blocks) {
  return blocks.map((block) => _plainTextBlock(block)).join('')
}

function _optionBlocks(option: unknown): Blocks {
  return Array.isArray(option)
    ? (option as Blocks)
    : option
      ? [option as Block]
      : []
}

function _plainTextBlock(block: Block): string {
  switch (block.type) {
    case 'text':
      return block.text
    case 'chars':
      return getTextFromChars(block)
    case 'br':
      return '\n'
    case 'question':
      return block.question ? _plainText(block.question) : ''
    case 'pageTitle':
      return block.title
    case 'cloze':
    case 'code':
    case 'l':
    case 'em':
    case 'comment':
    case 'a':
    case 'p':
    case 'quote':
    case 'alert':
    case 'rule':
    case 'questionGroup':
    case 'list':
    case 'li':
    case 'pre':
    case 'col':
    case 'cols':
    case 'definition':
      return 'children' in block && Array.isArray(block.children)
        ? _plainText(block.children)
        : ''
    case 'h':
      return _plainText(block.children)
    case 'mention':
      return block.subtype === 'date'
        ? new Date(block.date).toLocaleDateString()
        : `@${block.uid}`
    case 'table':
      return block.children
        .map((row) =>
          row.type === 'review'
            ? ''
            : row.children
                .map((cell) =>
                  cell.type === 'review' ? '' : _plainText(cell.children),
                )
                .join(' '),
        )
        .join('\n')
    case 'hr':
      return ''
    case 'comments':
    case 'criticComment':
    case 'diff':
    case 'review':
    case 'img':
    case 'video':
    case 'audio':
    case 'file':
    case 'preview':
    case 'page':
    case 'pageLink':
    case 'pages':
      return ''
    case 'pageMeta':
      return block.title
    case 'root':
    case 'td':
    case 'tr':
      return _plainText(block.children)
  }

  return ''
}
